// @flow
import * as Sentry from '@sentry/react'
import { ResourceSessionsContext } from 'components/ResourceFill/providers/ResourceSessionsProvider'
import { ResourceSharedContext } from 'components/ResourceFill/providers/ResourceSharedProvider'
import type { ResponseContext } from 'components/ResourceFill/types/response-context'
import {
  getPublicResponse,
  getResponse,
} from 'queries/responses/services/loadResponses'
import * as React from 'react'
import type { Response } from 'types/response'

type Props = {
  children: React.Node,
}

export const ResourceResponseContext: React.Context<?ResponseContext> =
  React.createContext(null)

function ResourceResponseProvider({ children }: Props): React.Node {
  const { sessions, sessionId } =
    React.useContext(ResourceSessionsContext) || {}

  const { mode, isTablet, source } =
    React.useContext(ResourceSharedContext) || {}

  const [error, setError] = React.useState(false)

  const [isFetching, setFetching] = React.useState(false)

  const [response, setResponse] = React.useState<Response>({
    answers: [],
    created_at: '',
    id: '',
    internal_user: null,
    is_archived: false,
    is_scheduled: false,
    is_submitted: false,
    name: '',
    uid: '',
  })

  const setPreviewResponseDetails = React.useCallback((response: Response) => {
    setResponse({
      ...response,
      answers: response?.answers || [],
    })
  }, [])

  const updateResponse = React.useCallback(
    async (index: number) => {
      if (!sessionId) return
      if (!sessions) return

      const currentSession = sessions[sessionId]
      const response = currentSession.responses?.[index]

      if (!response?.id) return

      if (mode === 'preview') {
        setPreviewResponseDetails(response)

        return
      }

      setFetching(true)

      try {
        const messageCode = currentSession?.messageCode
        if (
          !isTablet &&
          source !== 'kiosk' &&
          !['office', 'internal'].includes(mode)
        ) {
          const nextResponse = await getPublicResponse(response.id, {
            packetMessageCode: messageCode,
          })

          setResponse(nextResponse)
        } else {
          const nextResponse = await getResponse(response.id)

          setResponse(nextResponse)
        }
      } catch (e) {
        setError(true)
        Sentry.captureMessage('Error fetching response details', {
          extra: { error: e },
        })
      } finally {
        setFetching(false)
      }
      window.scrollTo({ top: 0, behavior: 'instant' })
    },
    [sessionId, sessions, mode, setPreviewResponseDetails, isTablet, source]
  )

  const value = React.useMemo(
    () => ({
      error,
      isFetching,
      sessions,
      response,
      setResponse,
      updateResponse,
    }),
    [error, isFetching, response, sessions, updateResponse]
  )

  return (
    <ResourceResponseContext.Provider value={value}>
      {children}
    </ResourceResponseContext.Provider>
  )
}

export default ResourceResponseProvider
